/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { buildBreadcrumbTrail } from '@/utils/breadcrumb-trail';
import { BREADCRUMB_SCROLL_ROW } from './styles';
import { cn } from '@/lib/utils';
import type { ShopperProducts } from '@/scapi';
import { routes, routeHref } from '@/route-paths';

/**
 * Hierarchical breadcrumb navigation for a category.
 *
 * Renders the category's primary category path — no `Home` entry and no product
 * name, per the design and the reporter's decision on CRCB2B-96.
 *
 * @param props - Component props
 * @param props.category - The category whose path is rendered
 * @param [props.leafIsCurrentPage] - Whether the final entry is the page being viewed.
 *   `true` (category and listing pages) renders it non-navigable and marked
 *   `aria-current="page"`. `false` (product pages, where the current page is the
 *   product and is not an entry) keeps it a link with no `aria-current`.
 * @returns The breadcrumb navigation landmark
 */
export default function CategoryBreadcrumbs({
    category,
    leafIsCurrentPage = true,
}: {
    category: ShopperProducts.schemas['Category'];
    leafIsCurrentPage?: boolean;
}): ReactElement {
    const { t } = useTranslation('category');
    const config = useConfig();
    const trail = buildBreadcrumbTrail(category, {
        rootCategoryId: config.pages.navigation.rootCategoryId,
    });

    // Does the trail hold anything a keyboard can land on? Only a listing page's current-page entry
    // is non-focusable (`<span role="link" aria-disabled>`), so a ONE-ENTRY listing trail — what
    // `buildBreadcrumbTrail` returns for a top-level category — has nothing. With a label wider than
    // the container that makes the row below a scroll region no keyboard can scroll: WCAG 2.1.1, axe
    // `scrollable-region-focusable` (serious), measured at 187px unreachable. See design.md §5.
    const hasNavigableEntry = trail.length > 1 || !leafIsCurrentPage;

    // No bottom margin — vertical spacing is the CALLER's, set with the parent container's `gap`, so
    // breadcrumbs join one rhythm with the content below instead of adding a margin on top of it
    // (CRCB2B-313). Pinned by a tripwire test, because a margin reintroduced during a rewrite of this
    // component does not surface as a merge conflict.
    return (
        // `w-full` is load-bearing, not cosmetic. A scroll container's min-content is its FULL
        // content width, so an ancestor sized to fit-content — the PLP title block is
        // `flex flex-col items-start` — inherits the whole trail's width and the PAGE scrolls
        // sideways instead of the row (measured: nav 726px inside a 343px column, document 421px
        // past the viewport). `min-w-0` and `max-w-full` do not fix it; both were measured.
        // Guarded by the `narrow-title-block` story. Derivation: design.md §2 in
        // `openspec/changes/archive/2026-08-13-breadcrumb-overflow-scroll/`.
        <Breadcrumb aria-label={t('breadcrumbs.label')} className="w-full">
            {/* The row's treatment — one line, overflow SCROLLED rather than clipped — lives in
                `./styles`, composed identically by the loading skeleton. Every part of it, and why,
                is documented there rather than here so the two sides cannot drift apart.

                `tabIndex` is the trail's own concern, not the row treatment's, so it stays here:
                the scroll container takes a tab stop ONLY when the trail holds nothing focusable
                (see `hasNavigableEntry` above). Giving it one unconditionally would add a tab stop
                to every page for a region a keyboard can already reach through its links, and one
                that on most pages does not scroll at all. */}
            <BreadcrumbList tabIndex={hasNavigableEntry ? undefined : 0} className={BREADCRUMB_SCROLL_ROW}>
                {trail.map((entry, index) => {
                    const isLeaf = index === trail.length - 1;

                    return (
                        // A category path cannot contain the same category twice, so the id
                        // (or the name, for entries SCAPI returns without one) is unique.
                        <Fragment key={entry.id ?? entry.name}>
                            {index > 0 && <BreadcrumbSeparator className="shrink-0" />}
                            <BreadcrumbItem className="shrink-0">
                                {isLeaf && leafIsCurrentPage ? (
                                    <BreadcrumbPage>{entry.name}</BreadcrumbPage>
                                ) : (
                                    <BreadcrumbLink asChild className={cn(isLeaf && 'text-foreground')}>
                                        <Link to={routeHref(routes.category, { categoryId: entry.id ?? '' })}>
                                            {entry.name}
                                        </Link>
                                    </BreadcrumbLink>
                                )}
                            </BreadcrumbItem>
                        </Fragment>
                    );
                })}
            </BreadcrumbList>
        </Breadcrumb>
    );
}
